import { useState } from 'react';
import { useApolloClient, useMutation } from 'react-apollo';
import pmap from 'p-map-browser';

import { cardUpdate as cardUpdateMutation } from '../../../apps/cards/graphql/mutations/card';
import { cardTemplateUpdate as cardTemplateUpdateMutation } from '../../../apps/cards/graphql/mutations/cardTemplate';
import useOpenAlert from '../../../hooks/useOpenAlert';
import { getErrorString } from '../../../util/normalize-errors';
import getPrintSizeCard from '../../../services/prints/graphql/queries/getPrintSizeCard';
import { getCardSide } from './helpers';
import { CARD_SIDE, PHOTO_TYPE } from '../../../services/cards/constants';
import { cardElementCreate as cardElementCreateMutation } from '../../../apps/cards/graphql/mutations/cardElement';
import { cardElementConfigurationCreate as cardElementConfigurationCreateMutation } from '../../../apps/cards/graphql/mutations/cardElementConfiguration';
import { isFullFrame } from '../../../services/cards/components/AccountCard/helpers';
import { DESIGN_MODES } from './constants';

const useCardSettings = () => {
  const [loadingCardSettings, setLoadingCardSettings] = useState(false);
  const [cardUpdate] = useMutation(cardUpdateMutation);
  const [cardTemplateUpdate] = useMutation(cardTemplateUpdateMutation);
  const [cardElementCreate] = useMutation(cardElementCreateMutation);
  const [cardElementConfigurationCreate] = useMutation(
    cardElementConfigurationCreateMutation,
  );
  const client = useApolloClient();
  const openAlert = useOpenAlert();

  const onCardSettingsUpdate = async ({
    cardId,
    cardTemplateId,
    printProductCardId,
    cardSide,
    values,
    designMode,
  }) => {
    if (loadingCardSettings) {
      return;
    }

    setLoadingCardSettings(true);

    try {
      if (cardId) {
        await cardUpdate({
          variables: {
            input: {
              card_id: cardId,
              name: values.card_name,
              ...values,
            },
          },
        });
      }

      if (cardTemplateId) {
        const {
          data: {
            cardTemplateUpdate: { card_template: cardTemplate },
          },
        } = await cardTemplateUpdate({
          variables: {
            input: {
              card_template_id: cardTemplateId,
              ...values,
            },
          },
        });

        if (cardSide && printProductCardId) {
          const {
            data: { card },
          } = await client.query({
            query: getPrintSizeCard,
            variables: {
              card_id: printProductCardId,
              print_size_id: values.print_size_id,
              orientation: values.orientation,
            },
          });

          const sizeCardSide = getCardSide({ card, side: CARD_SIDE.FRONT });
          const sizeNonEditableElementConfigs =
            sizeCardSide.card_element_configurations.filter(
              ({
                is_pro_editable: isEditable,
                photo_type: photoType,
                card_element: cardElement,
              }) =>
                !isEditable &&
                (photoType === PHOTO_TYPE.FOREGROUND ||
                  !isFullFrame(cardElement.frame)),
            );

          // We will remove any elements that is not pro editable, as these are intrinsic
          // to the templates they come from.
          const keptTemplateElements = cardTemplate.card_elements.filter(
            element => {
              const cardElementConfiguration =
                cardSide.card_element_configurations.find(
                  ({ card_element: elem }) => element.id === elem.id,
                );

              if (!cardElementConfiguration) {
                return true;
              }

              return cardElementConfiguration?.is_pro_editable;
            },
          );

          const newNonEditableElements = await pmap(
            sizeNonEditableElementConfigs,
            async sizeElementConfig => {
              const { card_element: element } = sizeElementConfig;

              const {
                data: {
                  cardElementCreate: { card_element: newElement },
                },
              } = await cardElementCreate({
                variables: {
                  input: {
                    type: element.type,
                    frame: { ...element.frame, __typename: undefined },
                    z_index: element.z_index,
                  },
                },
              });

              const photoElementConfiguration = (() => {
                if (sizeElementConfig?.photo_element_configuration) {
                  return sizeElementConfig?.photo_element_configuration;
                }

                if (sizeElementConfig?.asset_image) {
                  return {
                    photo_type: sizeElementConfig?.photo_type,
                    existing_asset_image_id: sizeElementConfig?.asset_image?.id,
                  };
                }

                if (designMode === DESIGN_MODES.ID_CARDS) {
                  return {
                    photo_type: sizeElementConfig?.photo_type,
                  };
                }

                return undefined;
              })();

              const textElementConfiguration = (() => {
                if (sizeElementConfig?.text_element_configuration) {
                  return sizeElementConfig?.text_element_configuration;
                }

                if (sizeElementConfig?.text_align) {
                  return {
                    text_align: sizeElementConfig.text_align,
                  };
                }
              })();

              return {
                element: newElement,
                cardElementConfiguration: {
                  is_editable: sizeElementConfig.is_editable,
                  is_printable: sizeElementConfig.is_printable,
                  is_pro_editable: sizeElementConfig.is_pro_editable,
                  help_placeholder: sizeElementConfig.help_placeholder,
                  photo_element_configuration: photoElementConfiguration,
                  text_element_configuration: textElementConfiguration,
                },
              };
            },
            { concurrency: 5 },
          );

          const cardElementIds = [
            ...keptTemplateElements.map(({ id }) => id),
            ...newNonEditableElements.map(({ element }) => element.id),
          ];

          await cardTemplateUpdate({
            variables: {
              input: {
                card_template_id: cardTemplateId,
                card_element_ids: cardElementIds,
              },
            },
          });

          //  Create configurations on the new card side matching those on the original
          await pmap(
            newNonEditableElements,
            async ({ element, cardElementConfiguration }) => {
              await cardElementConfigurationCreate({
                variables: {
                  input: {
                    card_side_id: cardSide.id,
                    card_element_id: element.id,
                    ...cardElementConfiguration,
                  },
                },
              });
            },
            { concurrency: 5 },
          );
        }
      }
    } catch (error) {
      openAlert({ type: 'error', message: getErrorString(error) });
    } finally {
      setLoadingCardSettings(false);
    }
  };

  return {
    onCardSettingsUpdate,
    loadingCardSettings,
    cardTemplateUpdate,
  };
};

export default useCardSettings;
